import { AfterViewInit, Component, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { DomHandler } from 'primeng/dom';
import * as uuid from 'uuid';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';

import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { Mention } from '../../../Mentions/models/mention';
import { KeywordHighlightModel } from '../../../Misc/models/keyword-highlight.model';
import { RightSidebarComponent } from '../../../Layout/components/RightSidebar/right-sidebar.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { EntityDetailsTabComponent } from '../../../TargetingShared/components/EntityDetailsTab/entity-details-tab.component';
import { BehaviorSubject, Subscription } from 'rxjs';
import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { MentionWorkbenchDialogComponent } from '../MentionWorkbenchDialog/mention-workbench-dialog.component';
import { animate, query, sequence, style, transition, trigger, AnimationEvent } from '@angular/animations';
import { ScrollPanelComponent } from '../../../../Common/Basic/components/ScrollPanel/scroll-panel.component';

enum MentionDetailsTabEnum {
    mentionDetails = 0,
    contact = 1,
    outlet = 2,
    mentionInsight = 3
}

@Component({
    selector: 'agpr-mention-details-take-over-panel',
    templateUrl: './mention-details-take-over-panel.component.html',
    styleUrls: ['./mention-details-take-over-panel.component.scss'],
    animations: [
        trigger(FullProfileDialogAnimationsEnum.slideInOutAnimation, [
            transition('* => open', [
                query(':self', [
                    style({ position: 'fixed', left: '100%', opacity: 0 }),
                    sequence([
                        animate('0.3s ease-out', style({ left: '0%', opacity: 1 }))
                    ])
                ]),
            ]),
            transition('* => close', [
                query(':self', [
                    style({ position: 'fixed', left: '0%', opacity: 1 }),
                    sequence([
                        animate('0.3s ease-in', style({ left: '100%', opacity: 0 })),
                    ])
                ])
            ])
        ]),
    ],
    standalone: false
})
export class MentionDetailsTakeOverPanelComponent extends AbstractDialogComponent implements  OnInit, AfterViewInit, OnDestroy {
    readonly entityTypes = EntityType;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly mentionDetailsTabEnum = MentionDetailsTabEnum;

    @Input() focusTab: 'contact' | 'outlet' | 'mention' = 'mention';
    @Input() fromReports: boolean = false;

    @ViewChild('rhpTabs') rhpTabs: RightSidebarComponent;
    @ViewChild('contactProfileTab') contactProfileTab: EntityDetailsTabComponent;
    @ViewChild('outletProfileTab') outletProfileTab: EntityDetailsTabComponent;
    @ViewChild('mentionScrollPanel') mentionScrollPanel: ScrollPanelComponent;

    zIndex: number;
    selectedTabIndex: number = 0;
    focusedMention: Mention;
    highlightKeywords: KeywordHighlightModel[] = [];
    isLoading: boolean = false;
    mieEnabled: boolean = true;

    subscriptions: Subscription[] = [];
    slideInOutAnimation: { value: OverlayAnimationStateEnum } = { value: OverlayAnimationStateEnum.open };
    mentions: Mention[] = [];
    isFirstMention: boolean = true;
    isLastMention: boolean = true;

    constructor(
        public coreDialogService: CoreDialogService,
        protected configService: ConfigService,
        private mentionsService: MentionsService,
        private injector: Injector,
    ) {
        super(coreDialogService, uuid.v4());
    }

    ngOnInit(): void {
        this.zIndex = ++DomHandler.zindex;
        if (this.configService.brandGUIDIsBurrelles() || this.configService.brandGUIDIsRefMedia()) {
            this.mieEnabled = false;
        }

        this.subscriptions.push(
            this.mentionsService.getMentions().subscribe(mentions => {
                this.mentions = mentions;
                this.updateNavigatorState();
            }),
            this.mentionsService.getFocusedMention().subscribe(x => {
                this.focusedMention = x;
                this.updateNavigatorState();
            }),
        );
    }

    ngAfterViewInit(): void {
        if (this.rhpTabs && this.contactProfileTab && this.outletProfileTab && this.focusTab !== 'mention') {
            requestAnimationFrame(() => {
                if (this.focusTab === 'contact') {
                    this.rhpTabs.selectTab(this.contactProfileTab);
                } else {
                    this.rhpTabs.selectTab(this.outletProfileTab);
                }
            });
        }
    }

    animationDone(event: AnimationEvent): void {
        if (event.toState === OverlayAnimationStateEnum.close) {
            super.closeDialog();
        }
    }

    expandClicked() {
        if (this.selectedTabIndex === MentionDetailsTabEnum.contact) {
            this.contactProfileTab.entityDetails.expandClicked();
        } else if (this.selectedTabIndex === MentionDetailsTabEnum.outlet) {
            this.outletProfileTab.entityDetails.expandClicked();
        }
    }

    public override closeDialog(closeAction?: any): void {
        this.slideInOutAnimation = { value: OverlayAnimationStateEnum.close };
    }

    public editSelectedMention(): void {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
                mention: this.focusedMention
            },
            outputs: {}
        };

        this.coreDialogService.open(MentionWorkbenchDialogComponent, ioModel, this.injector);
    }

    focusOutletTab(): void {
        this.rhpTabs.selectTab(this.outletProfileTab);
        this.selectedTabIndex = MentionDetailsTabEnum.outlet;
    }

    focusContactTab(): void {
        this.rhpTabs.selectTab(this.contactProfileTab);
        this.selectedTabIndex = MentionDetailsTabEnum.contact;
    }

    private updateNavigatorState(): void {
        const currentIndex = this.mentions.findIndex(
            m => m.id === this.focusedMention?.id
        );
        this.isFirstMention = currentIndex <= 0;
        this.isLastMention = currentIndex >= this.mentions.length - 1;
    }

    onPrevious(): void {
        const currentIndex = this.mentions.findIndex(
            m => m.id === this.focusedMention?.id
        );
        this.mentionsService.setFocusedMention(this.mentions[currentIndex - 1]);
        this.scrollToTop();
    }

    onNext(): void {
        const currentIndex = this.mentions.findIndex(
            m => m.id === this.focusedMention?.id
        );
        this.mentionsService.setFocusedMention(this.mentions[currentIndex + 1]);
        this.scrollToTop();
    }

    private scrollToTop(): void {
        this.mentionScrollPanel?.scrollTo(0, 0, false);
        this.rhpTabs?.scrollToTop();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }
}
